import React, { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { Ticket } from '../../features/groupTicket/types';
import {
    buildGroupTicketClassFilters,
    filterGroupTickets,
    getGroupTicketClassKey,
    GROUP_TICKET_CLASS_LABELS,
    GroupTicketClassKey,
    limitGroupTickets,
} from '../../features/groupTicket/ticketPicker';

type Props = {
    tickets: Ticket[];
    selectedTicketId: string;
    onSelect: (ticketId: string) => void;
};

/**
 * 수강권 카드와 같은 언어(그라디언트 헤더 + 지표 줄)로 그린다.
 * select의 한 줄짜리 이름 목록은 상품 수가 늘어날수록 고를 수 없는 목록이 된다.
 */
const GroupTicketOptionCard = ({
    ticket,
    isSelected,
    onSelect,
}: {
    ticket: Ticket;
    isSelected: boolean;
    onSelect: () => void;
}) => {
    const classTypeLabel = GROUP_TICKET_CLASS_LABELS[getGroupTicketClassKey(ticket)];
    const countLabel =
        typeof ticket.totalCount === 'number' && ticket.totalCount > 0
            ? `${ticket.totalCount}회`
            : '횟수 없음';
    const periodLabel =
        typeof ticket.periodDays === 'number' && ticket.periodDays > 0
            ? `${ticket.periodDays}일`
            : '기간 없음';

    return (
        <button
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={onSelect}
            style={{
                textAlign: 'left',
                borderRadius: 10,
                overflow: 'hidden',
                cursor: 'pointer',
                backgroundColor: '#FFFFFF',
                // 선택을 테두리 색만으로 알리면 색각 이상에서 구분되지 않는다. 굵기·그림자·체크를 함께 쓴다.
                border: `1px solid ${isSelected ? '#2563EB' : '#E3E8F0'}`,
                boxShadow: isSelected
                    ? 'inset 0 0 0 1px #2563EB, 0 6px 16px rgba(37,99,235,0.14)'
                    : '0 1px 2px rgba(16,24,40,0.04)',
                transition: 'border-color .15s ease, box-shadow .15s ease',
            }}
        >
            <div
                style={{
                    position: 'relative',
                    padding: '12px 14px',
                    background: isSelected
                        ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                        : 'linear-gradient(135deg, #94A3B8 0%, #64748B 100%)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: 22 }}>
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#FFFFFF',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {ticket.name}
                    </span>
                    <span
                        style={{
                            flexShrink: 0,
                            fontSize: 10,
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: 4,
                            backgroundColor: 'rgba(255,255,255,0.24)',
                            color: '#FFFFFF',
                        }}
                    >
                        {classTypeLabel}
                    </span>
                </div>
                {isSelected && (
                    <span
                        aria-hidden
                        style={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            width: 18,
                            height: 18,
                            borderRadius: 999,
                            backgroundColor: '#FFFFFF',
                            color: '#1D4ED8',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Check size={12} strokeWidth={3.5} />
                    </span>
                )}
            </div>

            <div
                style={{
                    display: 'flex',
                    gap: 12,
                    padding: '10px 14px 12px',
                    backgroundColor: '#FAFBFD',
                }}
            >
                <TicketStat label="총 회차" value={countLabel} />
                <TicketStat label="유효 기간" value={periodLabel} />
                <TicketStat label="가격" value={formatPrice(ticket.price)} />
            </div>
        </button>
    );
};

const TicketStat = ({ label, value }: { label: string; value: string }) => (
    <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 500 }}>{label}</div>
        <div
            style={{
                marginTop: 2,
                fontSize: 12,
                fontWeight: 700,
                color: '#111827',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            {value}
        </div>
    </div>
);

// lib/utils의 convertToKRW를 쓰지 않는다 — 그 배럴이 react-secure-storage를 끌고 와
// 이 컴포넌트를 단독으로 테스트할 수 없게 만든다. 포맷은 같다.
const formatPrice = (price: number) => `${(price ?? 0).toLocaleString('ko-KR')}원`;

const chipStyle = (isActive: boolean): React.CSSProperties => ({
    height: 30,
    padding: '0 12px',
    borderRadius: 999,
    border: `1px solid ${isActive ? '#2563EB' : '#DCE3ED'}`,
    backgroundColor: isActive ? '#2563EB' : '#FFFFFF',
    color: isActive ? '#FFFFFF' : '#5B6472',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
});

/**
 * 상품이 수십~수백 개인 센터에서도 고를 수 있게 검색·유형 필터를 함께 둔다.
 * 서버가 센터 상품 전체를 한 번에 내려주므로 걸러내는 일은 화면 몫이다.
 */
const GroupTicketPicker = ({ tickets, selectedTicketId, onSelect }: Props) => {
    const [search, setSearch] = useState('');
    const [classFilter, setClassFilter] = useState<GroupTicketClassKey | null>(null);

    const classFilters = useMemo(() => buildGroupTicketClassFilters(tickets), [tickets]);
    const filtered = useMemo(
        () => filterGroupTickets({ tickets, search, classFilter }),
        [tickets, search, classFilter],
    );
    const { visible, hiddenCount } = useMemo(
        () => limitGroupTickets(filtered, selectedTicketId),
        [filtered, selectedTicketId],
    );
    // 칩은 유형이 2개 이상일 때만 의미가 있다. 하나뿐이면 누를 이유가 없는 버튼이 된다.
    const showClassFilters = classFilters.length > 1;
    // 몇 개 안 되면 검색창이 오히려 방해다.
    const showSearch = tickets.length > 6;

    if (tickets.length === 0) {
        return (
            <div
                style={{
                    borderRadius: 8,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#F9FAFB',
                    padding: '20px 16px',
                    textAlign: 'center',
                }}
            >
                <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: '#6B7280' }}>
                    사용할 수 있는 그룹 수강권 상품이 없습니다.
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12, lineHeight: '18px', color: '#98A2B3' }}>
                    수강권 관리에서 그룹 상품을 먼저 만들어주세요.
                </p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {showSearch && (
                <div style={{ position: 'relative' }}>
                    <span
                        aria-hidden
                        style={{
                            position: 'absolute',
                            left: 12,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#98A2B3',
                            lineHeight: 0,
                        }}
                    >
                        <Search size={15} />
                    </span>
                    <input
                        aria-label="그룹 수강권 상품 검색"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="상품명 또는 회차로 검색 (예: 8대1, 20회)"
                        style={{
                            width: '100%',
                            height: 40,
                            borderRadius: 8,
                            border: '1px solid #DCE3ED',
                            padding: '0 12px 0 34px',
                            fontSize: 14,
                            color: '#313640',
                            backgroundColor: '#FFFFFF',
                        }}
                    />
                </div>
            )}

            {showClassFilters && (
                <div role="group" aria-label="수업 유형" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    <button
                        type="button"
                        aria-pressed={classFilter === null}
                        onClick={() => setClassFilter(null)}
                        style={chipStyle(classFilter === null)}
                    >
                        전체 {tickets.length}
                    </button>
                    {classFilters.map((filter) => (
                        <button
                            key={filter.key}
                            type="button"
                            aria-pressed={classFilter === filter.key}
                            onClick={() => setClassFilter(filter.key)}
                            style={chipStyle(classFilter === filter.key)}
                        >
                            {filter.label} {filter.count}
                        </button>
                    ))}
                </div>
            )}

            {filtered.length === 0 ? (
                <div
                    style={{
                        borderRadius: 8,
                        border: '1px solid #E5E7EB',
                        backgroundColor: '#F9FAFB',
                        padding: '20px 16px',
                        textAlign: 'center',
                    }}
                >
                    <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: '#6B7280' }}>
                        조건에 맞는 상품이 없습니다.
                    </p>
                    <button
                        type="button"
                        onClick={() => {
                            setSearch('');
                            setClassFilter(null);
                        }}
                        style={{
                            marginTop: 8,
                            height: 30,
                            padding: '0 12px',
                            borderRadius: 8,
                            border: '1px solid #C8D4E8',
                            backgroundColor: '#FFFFFF',
                            color: '#2B529D',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}
                    >
                        조건 지우기
                    </button>
                </div>
            ) : (
                <>
                    <div
                        role="radiogroup"
                        aria-label="전환할 그룹 수강권 상품"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: 10,
                            // 상품이 많은 센터에서 모달이 화면 밖으로 밀리지 않게 스크롤로 가둔다.
                            maxHeight: 268,
                            overflowY: 'auto',
                            paddingRight: 2,
                        }}
                    >
                        {visible.map((ticket) => (
                            <GroupTicketOptionCard
                                key={ticket._id}
                                ticket={ticket}
                                isSelected={selectedTicketId === ticket._id}
                                onSelect={() => onSelect(ticket._id)}
                            />
                        ))}
                    </div>

                    {/* 잘라낸 사실을 숨기면 "내 상품이 없어졌다"가 된다. 몇 개가 남았는지 밝힌다. */}
                    {hiddenCount > 0 && (
                        <p style={{ margin: 0, fontSize: 12, lineHeight: '18px', color: '#8A5A00' }}>
                            {hiddenCount}개를 더 찾을 수 있습니다. 검색이나 수업 유형으로 좁혀주세요.
                        </p>
                    )}
                </>
            )}
        </div>
    );
};

export default GroupTicketPicker;
